import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { useDashboard } from '@/hooks/useDashboard'
import { useActivity } from '@/hooks/useActivity'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { t } from '@/lib/i18n'
import { api, ApiError } from '@/lib/api'
import { formatMoney, formatRelativeTime, currentMonthLabel } from '@/lib/format'
import type { LedgerCategory, ActivityEntry, ProjectTraffic } from '@/types/api'
import styles from './DashboardPage.module.css'

/** Color per cost category — matches design-brief. */
const CATEGORY_COLORS: Partial<Record<LedgerCategory, string>> = {
  ai_tokens: 'var(--color-accent)',
  infrastructure: 'var(--color-primary)',
  domain_ssl: 'var(--color-violet)',
  saas_subscription: 'var(--color-indigo)',
  payment_fee: 'var(--color-slate)',
  other: '#94a3b8',
}

const CATEGORY_LABELS: Partial<Record<LedgerCategory, string>> = {
  ai_tokens: 'AI 토큰',
  infrastructure: 'Cloudflare',
  domain_ssl: '도메인/SSL',
  saas_subscription: 'SaaS 구독',
  payment_fee: '결제 수수료',
  other: '기타',
}

function categoryColor(c: LedgerCategory): string {
  return CATEGORY_COLORS[c] ?? '#94a3b8'
}

function categoryLabel(c: LedgerCategory): string {
  return CATEGORY_LABELS[c] ?? c
}

// ─── Activity feed helpers ───

const ACTIVITY_DOT_COLOR: Record<string, string> = {
  ledger: 'var(--color-accent)',
  incident: 'var(--color-danger)',
  asset: 'var(--color-cost)',
  project: 'var(--color-primary)',
}

function activityDotColor(entityType: string | null): string {
  return (entityType && ACTIVITY_DOT_COLOR[entityType]) || '#94a3b8'
}

function safeParseMeta(json: string): Record<string, unknown> {
  try {
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return {}
  }
}

/** Map an activity action + metadata to a human-readable label and detail. */
function describeActivity(entry: ActivityEntry): { label: string; detail?: string } {
  const meta = safeParseMeta(entry.metadataJson)
  switch (entry.action) {
    case 'ledger.entry.created': {
      const dir = meta.direction === 'inbound' ? '수익' : '비용'
      const amount =
        typeof meta.amount === 'number' ? formatMoney(meta.amount, { signed: true }) : null
      return { label: `${dir} 항목 기록됨`, detail: amount ?? undefined }
    }
    case 'incident.opened':
      return {
        label: '인시던트 발생',
        detail: typeof meta.title === 'string' ? meta.title : undefined,
      }
    case 'asset.expiring': {
      const parts = [
        typeof meta.name === 'string' ? meta.name : null,
        typeof meta.days === 'number' ? `${meta.days}일 남음` : null,
      ].filter(Boolean)
      return { label: '에셋 만료 임박', detail: parts.join(' · ') || undefined }
    }
    case 'project.created':
      return {
        label: '프로젝트 생성됨',
        detail: typeof meta.slug === 'string' ? meta.slug : undefined,
      }
    default:
      return { label: entry.action }
  }
}

function actorLabel(actor: string): string {
  if (actor === 'system') return '시스템'
  if (actor === 'manual') return '수동'
  if (actor.startsWith('webhook:')) {
    const src = actor.slice('webhook:'.length)
    return src.charAt(0).toUpperCase() + src.slice(1)
  }
  return actor
}

/** Build a conic-gradient string from cost breakdown segments. */
function buildDonutGradient(
  segments: Array<{ category: LedgerCategory; pct: number }>,
): string {
  if (segments.length === 0) return 'var(--color-surface-alt)'
  let acc = 0
  const stops = segments.map((s) => {
    const start = (acc / 100) * 360
    acc += s.pct
    const end = (acc / 100) * 360
    return `${categoryColor(s.category)} ${start}deg ${end}deg`
  })
  return `conic-gradient(${stops.join(', ')})`
}

export default function DashboardPage() {
  const { data, isLoading, error, refetch } = useDashboard()
  const {
    data: activity,
    isLoading: activityLoading,
    error: activityError,
    refetch: activityRefetch,
  } = useActivity(10)

  if (isLoading) {
    return (
      <>
        <Loading variant="block" />
        <Loading variant="cards" count={3} />
      </>
    )
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />
  }

  if (!data) {
    return <EmptyState title="대시보드 데이터가 없습니다" description="잠시 후 다시 시도해주세요." />
  }

  const { netBurn, costBreakdown, projectPnl, monitors, openIncidents, expiringAssets } = data
  const monthLabel = currentMonthLabel()
  const isPositive = netBurn.currentMonthNetCents >= 0
  const hasData = netBurn.currentMonthInboundCents > 0 || netBurn.currentMonthOutboundCents > 0

  return (
    <>
      {/* Alert banner — only when something needs attention */}
      {(openIncidents > 0 || expiringAssets > 0) && (
        <div className={styles.alertBanner}>
          {openIncidents > 0 && (
            <div className={`${styles.alertItem} ${styles.alertCritical}`}>
              <span className={styles.alertIcon}>⚠</span>
              <span className={styles.alertMsg}>
                {openIncidents}개 인시던트 열려 있음
              </span>
              <Link to="/incidents" className={styles.alertLink}>
                인시던트 보기 →
              </Link>
            </div>
          )}
          {expiringAssets > 0 && (
            <div className={`${styles.alertItem} ${styles.alertWarning}`}>
              <span className={styles.alertIcon}>🔔</span>
              <span className={styles.alertMsg}>{expiringAssets}개 에셋 만료 임박 (30일 내)</span>
              <Link to="/assets" className={styles.alertLink}>
                에셋 보기 →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Hero grid */}
      <div className={styles.heroGrid}>
        {/* Net burn */}
        <div className={`${styles.heroCard} ${styles.heroCardBurn}`}>
          <span className={styles.burnOverline}>{t('Net burn this month')} · {monthLabel}</span>
          <span
            className={`${styles.burnValue} ${
              !hasData
                ? styles.burnValueNeutral
                : isPositive
                  ? styles.burnValuePositive
                  : styles.burnValueNegative
            }`}
          >
            {formatMoney(netBurn.currentMonthNetCents, { signed: true })}
          </span>
          {netBurn.trendPct !== null ? (
            <span
              className={`${styles.burnBadge} ${
                netBurn.trendPct >= 0 ? styles.burnBadgeUp : styles.burnBadgeDown
              }`}
            >
              {netBurn.trendPct >= 0 ? '▲' : '▼'} {Math.abs(netBurn.trendPct)}% vs上月
            </span>
          ) : (
            <span className={`${styles.burnBadge} ${styles.burnBadgeNeutral}`}>{t('First month')}</span>
          )}
          <div className={styles.burnFooter}>
            <div className={styles.burnFooterItem}>
              <span className={styles.burnFooterLabel}>{t('Revenue')}</span>
              <span className={`${styles.burnFooterValue} ${styles.valuePositive}`}>
                {formatMoney(netBurn.currentMonthInboundCents, { signed: true })}
              </span>
            </div>
            <div className={styles.burnFooterItem}>
              <span className={styles.burnFooterLabel}>{t('Cost')}</span>
              <span className={`${styles.burnFooterValue} ${styles.valueNegative}`}>
                {formatMoney(netBurn.currentMonthOutboundCents, { signed: true })}
              </span>
            </div>
            <div className={styles.burnFooterItem}>
              <span className={styles.burnFooterLabel}>{t('Previous month net')}</span>
              <span className={styles.burnFooterValue}>
                {formatMoney(netBurn.previousMonthNetCents, { signed: true })}
              </span>
            </div>
          </div>
        </div>

        {/* Monitor summary */}
        <div className={styles.heroCard}>
          <div className={styles.cardHeaderRow}>
            <span className={styles.cardTitle}>모니터</span>
            {monitors.total > 0 && (
              <span className={`${styles.badge} ${styles.badgeOk}`}>
                {monitors.up}/{monitors.total} 정상
              </span>
            )}
          </div>
          {monitors.down > 0 ? (
            <span className={styles.downValue}>{monitors.down} down</span>
          ) : monitors.total > 0 ? (
            <span className={styles.allOkValue}>전체 정상</span>
          ) : (
            <span className={styles.allOkValue}>—</span>
          )}
        </div>

        {/* Expiring assets */}
        <div className={styles.heroCard}>
          <div className={styles.cardHeaderRow}>
            <span className={styles.cardTitle}>에셋 만료</span>
            {expiringAssets > 0 && (
              <span className={`${styles.badge} ${styles.badgeWarn}`}>30일 내 {expiringAssets}건</span>
            )}
          </div>
          {expiringAssets > 0 ? (
            <span className={styles.downValue} style={{ color: 'var(--color-cost-text)' }}>
              {expiringAssets}건
            </span>
          ) : (
            <span className={styles.allOkValue}>안전</span>
          )}
        </div>
      </div>

      {/* Cost breakdown */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t('Cost breakdown')}</h2>
          <Link to="/ledger" style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-accent)' }}>
            {t('View details')} →
          </Link>
        </div>
        {costBreakdown.length === 0 ? (
          <EmptyState title="이번 달 비용 데이터가 없습니다" description="원장 항목이 추가되면 여기에 표시됩니다." />
        ) : (
          <div className={styles.costBody}>
            <div className={styles.donutWrap}>
              <div
                style={{
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: buildDonutGradient(costBreakdown),
                }}
                role="img"
                aria-label="비용 분류 도넛 차트"
              />
              <div className={styles.donutCenter}>
                <span className={styles.donutValue}>
                  {formatMoney(netBurn.currentMonthOutboundCents)}
                </span>
                <span className={styles.donutLabel}>이번 달</span>
              </div>
            </div>
            <div className={styles.legend}>
              {costBreakdown.map((row) => (
                <div key={row.category} className={styles.legendRow}>
                  <span
                    className={styles.legendDot}
                    style={{ background: categoryColor(row.category) }}
                  />
                  <span className={styles.legendName}>{categoryLabel(row.category)}</span>
                  <span className={styles.legendPct}>{row.pct}%</span>
                  <span className={styles.legendAmount}>{formatMoney(row.amountCents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Project P&L */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t('Project P&L')}</h2>
          <Link to="/projects" style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-accent)' }}>
            {t('View all')} →
          </Link>
        </div>
        {projectPnl.length === 0 ? (
          <EmptyState
            title="등록된 프로젝트가 없습니다"
            description="첫 프로젝트를 추가해 운영을 시작하세요."
            action={<Link to="/projects" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>프로젝트 추가 →</Link>}
          />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>프로젝트</th>
                  <th>수익</th>
                  <th>비용</th>
                  <th>NET</th>
                </tr>
              </thead>
              <tbody>
                {projectPnl.map((p) => (
                  <tr key={p.projectId}>
                    <td>
                      <div className={styles.projCell}>
                        <span className={styles.statusDot} style={{ background: 'var(--color-revenue)' }} />
                        <span className={styles.projName}>{p.projectName}</span>
                      </div>
                    </td>
                    <td className={`${styles.amountCell} ${styles.valuePositive}`}>
                      {formatMoney(p.inboundCents, { signed: true })}
                    </td>
                    <td className={`${styles.amountCell} ${styles.valueNegative}`}>
                      {formatMoney(p.outboundCents, { signed: true })}
                    </td>
                    <td
                      className={`${styles.amountCell} ${
                        p.netCents >= 0 ? styles.valuePositive : ''
                      }`}
                      style={p.netCents < 0 ? { color: 'var(--color-danger-text)' } : undefined}
                    >
                      {formatMoney(p.netCents, { signed: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Traffic — Cloudflare Analytics */}
      <TrafficSection />

      {/* Recent activity */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t('Recent activity')}</h2>
        </div>
        {activityLoading ? (
          <Loading variant="rows" count={5} />
        ) : activityError ? (
          <ErrorState error={activityError} onRetry={() => activityRefetch()} />
        ) : !activity || activity.length === 0 ? (
          <EmptyState
            title={t('No recent activity')}
            description="활동 내역이 기록되면 여기에 표시됩니다."
          />
        ) : (
          <ul className={styles.activityList}>
            {activity.map((entry) => {
              const desc = describeActivity(entry)
              return (
                <li key={entry.id} className={styles.activityItem}>
                  <span
                    className={styles.activityDot}
                    style={{ background: activityDotColor(entry.entityType) }}
                    aria-hidden="true"
                  />
                  <div className={styles.activityBody}>
                    <span className={styles.activityLabel}>{desc.label}</span>
                    {desc.detail && (
                      <span className={styles.activityDetail}>{desc.detail}</span>
                    )}
                  </div>
                  <div className={styles.activityMeta}>
                    <span className={styles.activityActor}>{actorLabel(entry.actor)}</span>
                    <span className={styles.activityTime}>
                      {formatRelativeTime(entry.occurredAt)}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </>
  )
}

// ─── Traffic section (Cloudflare Analytics) ───

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

function TrafficSection() {
  // ref toggled only while a manual refresh is in flight so window-focus
  // refetches still hit the KV cache instead of the CF API.
  const refreshNowRef = useRef(false)
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['traffic', 7],
    queryFn: () => api.traffic.list(7, refreshNowRef.current),
  })

  async function handleRefresh() {
    refreshNowRef.current = true
    try {
      await refetch()
    } finally {
      refreshNowRef.current = false
    }
  }

  if (isLoading) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>트래픽</h2>
        </div>
        <Loading variant="rows" count={3} />
      </section>
    )
  }

  if (error) {
    const cfNotConfigured = error instanceof ApiError && error.code === 'CF_NOT_CONFIGURED'
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>트래픽</h2>
        </div>
        {cfNotConfigured ? (
          <div className={styles.trafficCtaBox}>
            <p className={styles.trafficCtaMsg}>
              Cloudflare Analytics 연동이 필요합니다. 설정에서 API 토큰을 등록하세요.
            </p>
            <Link to="/settings" className={styles.trafficCtaLink}>
              Cloudflare 설정 →
            </Link>
          </div>
        ) : (
          <ErrorState error={error} onRetry={() => refetch()} />
        )}
      </section>
    )
  }

  const summary = data?.data
  if (!summary || summary.projects.length === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>트래픽</h2>
        </div>
        <EmptyState
          title="트래픽 데이터가 없습니다"
          description="프로젝트에 Cloudflare 스크립트를 매핑하면 최근 7일 트래픽이 표시됩니다."
        />
      </section>
    )
  }

  const maxRequests = Math.max(1, ...summary.projects.map((p) => p.totalRequests))

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>
          트래픽
          <span className={styles.trafficRange}>최근 7일</span>
        </h2>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isFetching}
          className={styles.refreshBtn}
        >
          {isFetching ? '불러오는 중…' : '↻ 새로고침'}
        </button>
      </div>

      <div className={styles.trafficTotals}>
        <div className={styles.trafficTotal}>
          <span className={styles.trafficTotalLabel}>총 요청</span>
          <span className={styles.trafficTotalValue}>{formatNumber(summary.totalRequests)}</span>
        </div>
        <div className={styles.trafficTotal}>
          <span className={styles.trafficTotalLabel}>총 에러</span>
          <span className={`${styles.trafficTotalValue} ${styles.trafficTotalValueErr}`}>
            {formatNumber(summary.totalErrors)}
          </span>
        </div>
      </div>

      <div className={styles.trafficBars}>
        {summary.projects.map((p: ProjectTraffic) => (
          <TrafficBar key={p.projectId} project={p} maxRequests={maxRequests} />
        ))}
      </div>
    </section>
  )
}

function TrafficBar({ project, maxRequests }: { project: ProjectTraffic; maxRequests: number }) {
  const reqPct = Math.max(2, (project.totalRequests / maxRequests) * 100)
  // Error share is relative to this project's own requests, rendered as the
  // trailing segment of the bar so users can spot high-error projects at a glance.
  const errPctOfReq =
    project.totalRequests > 0
      ? Math.min(100, (project.totalErrors / project.totalRequests) * 100)
      : 0

  return (
    <div className={styles.trafficRow}>
      <div className={styles.trafficRowHead}>
        <span className={styles.trafficProjName}>{project.projectName}</span>
        <span className={styles.trafficRowNums}>
          <span className={styles.trafficReqNum}>{formatNumber(project.totalRequests)}</span>
          {project.totalErrors > 0 && (
            <span className={styles.trafficErrNum}>
              {formatNumber(project.totalErrors)} err
            </span>
          )}
        </span>
      </div>
      <div className={styles.trafficBarTrack} role="img" aria-label={`${project.projectName}: ${formatNumber(project.totalRequests)} requests`}>
        <div className={styles.trafficBarFill} style={{ width: `${reqPct}%` }}>
          {errPctOfReq > 0 && (
            <div className={styles.trafficBarErr} style={{ width: `${errPctOfReq}%` }} />
          )}
        </div>
      </div>
    </div>
  )
}
